import {useState, useEffect} from "react";
import {
    Button, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, TextField,
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {TemplateType} from "../types";
import {getChannelNameConflicts, CHANNEL_FORBIDDEN_TOKENS} from "../utils/channelNameValidation";

interface DuplicateTemplateDialogProps {
    open: boolean;
    originalName: string;
    templateType?: string;
    onClose: () => void;
    onConfirm: (newName: string) => void;
    loading?: boolean;
}

const DuplicateTemplateDialog = ({open, originalName, templateType, onClose, onConfirm, loading}: DuplicateTemplateDialogProps) => {
    const {t} = useTranslation("templates");
    const [newName, setNewName] = useState("");
    const [error, setError] = useState("");

    // Helper function to generate the default duplicate name
    const generateDuplicateName = (name: string): string => {
        // Check if the name ends with a tenant suffix pattern (_xx where xx is 2 letters)
        const suffixMatch = name.match(/^(.+)(_[a-z]{2})$/i);
        if (suffixMatch) {
            // Insert "-copy" before the suffix
            const baseName = suffixMatch[1];
            const suffix = suffixMatch[2];
            return `${baseName}-copy${suffix}`;
        }
        // If no valid suffix found, still append "-copy" but validation will catch it
        return `${name}-copy`;
    };

    useEffect(() => {
        if (open) {
            setNewName(generateDuplicateName(originalName));
            setError("");
        }
    }, [open, originalName]);

    const validate = (value: string): string => {
        if (!value.trim()) return t('duplicateDialog.validationRequired');
        if (!/^[a-zA-Z0-9]/.test(value)) return t('duplicateDialog.validationStartChar');
        if (!/^[a-zA-Z0-9_\-]+$/.test(value)) return t('duplicateDialog.validationChars');
        if (value.length > 128) return t('duplicateDialog.validationMaxLength');
        
        // Validate suffix: must end with _xx where xx is a valid country code
        const suffixMatch = value.match(/_([a-z]{2})$/i);
        if (!suffixMatch) {
            return t('duplicateDialog.validationMissingSuffix');
        }
        const suffix = suffixMatch[1].toLowerCase();
        const validSuffixes = ['it', 'es', 'fr', 'pt', 'en'];
        if (!validSuffixes.includes(suffix)) {
            return t('duplicateDialog.validationInvalidSuffix', { suffixes: validSuffixes.map(s => '_' + s).join(', ') });
        }

        // Channel-name conflict: il nome non deve contenere token di un canale diverso
        if (templateType) {
            const conflicts = getChannelNameConflicts(value, templateType as TemplateType);
            if (conflicts.length > 0) {
                return t('duplicateDialog.validationChannelConflict', {
                    channel: templateType,
                    forbidden: (CHANNEL_FORBIDDEN_TOKENS[templateType as TemplateType] || []).join(', '),
                });
            }
        }
        
        return "";
    };

    const handleChange = (value: string) => {
        setNewName(value);
        setError(validate(value));
    };

    const handleConfirm = () => {
        const err = validate(newName);
        if (err) { setError(err); return; }
        onConfirm(newName.trim());
    };

    const typeLabel = templateType ? templateType.toLowerCase() : "template";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                {t('duplicateDialog.title', {type: typeLabel})}
                <IconButton size="small" onClick={onClose}><Close/></IconButton>
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus fullWidth size="small"
                    label={t('duplicateDialog.nameLabel')}
                    value={newName}
                    onChange={(e) => handleChange(e.target.value)}
                    error={!!error}
                    helperText={error || t('duplicateDialog.nameHelper')}
                    sx={{mt: 1}}
                    inputProps={{maxLength: 128}}
                    onKeyDown={(e) => { if (e.key === "Enter" && !error) handleConfirm(); }}
                />
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button variant="outlined" onClick={onClose}>{t('duplicateDialog.cancel')}</Button>
                <Button variant="contained" color="accent" onClick={handleConfirm} disabled={!!error || loading}>
                    {loading ? t('duplicateDialog.loading') : t('duplicateDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateTemplateDialog;
